import { useAnalytics } from '@/contexts/AnalyticsContext';
import {
  ArrowDownIcon,
  ArrowDownLeftIcon,
  ArrowDownRightIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowUpIcon,
  ArrowUpLeftIcon,
  ArrowUpRightIcon,
  ChevronLeftIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import {
  PopupData,
  ScrollDepthPerDevice,
  SectionBackgroundGradient,
  SectionData,
  Website,
} from '@superfunnel/types';
import ColorPickerField from '@superfunnel/ui-core/ColorPickerField';
import MenuTabs, { MenuTab } from '@superfunnel/ui-core/MenuTabs';
import Button from '@superfunnel/ui-core/v2/Button';
import CodeInput from '@superfunnel/ui-core/v2/CodeInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@superfunnel/ui-core/v2/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { Separator } from '@superfunnel/ui-core/v2/Separator';
import Switch from '@superfunnel/ui-core/v2/Switch';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@superfunnel/ui-core/v2/ToggleGroup';
import { Transition } from '@superfunnel/ui-core/v2/Transition';
import {
  CursorPointer,
  EditPencil,
  Eye,
  EyeClosed,
  Laptop,
  MoreVert,
  MouseScrollWheel,
  SmartphoneDevice,
  Timer,
  Trash,
} from 'iconoir-react';
import { TabletIcon } from 'lucide-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDashboardContext } from '../../contexts/DashboardContext';
import {
  WebsiteUpdate,
  WebsiteUpdaterContext,
} from '../../contexts/WebsiteUpdaterContext';
import { capitalize, classNames, sendMessageToIframe } from '../../utils';
import FileUploadInput from '../FormFields/FileUpload/FileUploadInput';
import { SectionStructureForm } from '../SectionStructureForm/SectionStructureForm';
import SettingsHelpTooltip from '../settings/SettingsHelpTooltip';
import SortableList from '../SortableList';
import { useEditor } from './EditorContext';

type EditorPopupMode = 'list' | 'edit';

interface Props {
  open?: boolean;
  onClose: () => void;
  section?: SectionData;
  onChange: (_: SectionData) => void;
  mode?: EditorPopupMode;
  website?: Website;
  websiteId?: string;
  onUpdateWebsiteEvent?: (_: WebsiteUpdate) => void;
}
export default function EditorPopup({
  open,
  onClose,
  section,
  onChange: _onChange,
  mode: initialMode = 'edit',
  website,
  websiteId,
  onUpdateWebsiteEvent,
}: Props) {
  const { track } = useAnalytics();
  const [selectedTab, setSelectedTab] = useState('content');
  const [currentMode, setCurrentMode] = useState<EditorPopupMode>(initialMode);
  const [selectedSection, setSelectedSection] = useState<
    SectionData | undefined
  >(section);
  const [selectedPopup, setSelectedPopup] = useState<PopupData | undefined>(
    undefined,
  );
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [sectionToRename, setSectionToRename] = useState<
    SectionData | undefined
  >(undefined);
  const [previewingPopupIds, setPreviewingPopupIds] = useState<Set<string>>(
    new Set(),
  );

  const { dynamicPageId, setDeleteSectionModalOpen, setActiveWebsiteUpdate } =
    useEditor();
  const { createWebsiteUpdate } = useContext(WebsiteUpdaterContext);

  // Update mode when prop changes
  useEffect(() => {
    setCurrentMode(initialMode);
  }, [initialMode]);

  // Update selected section when prop changes
  useEffect(() => {
    setSelectedSection(section);
  }, [section]);

  const sections = useMemo(() => {
    return website?.development?.sections ?? [];
  }, [website?.development?.sections]);

  const tabs: MenuTab[] = useMemo(() => {
    if (currentMode === 'list') {
      return [{ id: 'list', name: 'Sections', current: true }];
    }

    // Popup edit mode: Triggers + Content tabs
    if (selectedPopup) {
      return [
        {
          id: 'triggers',
          name: 'Triggers',
          current: selectedTab === 'triggers',
        },
        { id: 'content', name: 'Content', current: selectedTab === 'content' },
      ];
    }

    // Section edit mode: Content + Style tabs
    const tabs = [
      { id: 'content', name: 'Content', current: false },
      { id: 'style', name: 'Style', current: false },
    ].map((tab) => ({
      ...tab,
      current: tab.id === selectedTab,
    }));

    return tabs;
  }, [selectedTab, currentMode, selectedPopup]);

  const [position, setPosition] = useState<'right' | 'left'>('right');

  const onChange = useCallback(
    (updatedSection: SectionData) => {
      console.log('onChanged triggered');
      _onChange(updatedSection);
      setSelectedSection(updatedSection);
    },
    [_onChange],
  );

  const onSectionClick = useCallback(
    (clickedSection: SectionData) => {
      track('editor_popup_section_clicked', {
        section_id: clickedSection.id,
        section_type: clickedSection.type,
        section_variant: clickedSection.variant,
      });

      sendMessageToIframe({
        eventName: 'jumpTo',
        selector: `#section-${clickedSection.type}-${clickedSection.variant}-${clickedSection.id}`,
      });

      setSelectedSection(clickedSection);
      setSelectedPopup(undefined);
      setCurrentMode('edit');
      setSelectedTab('content');
    },
    [track],
  );

  const onPopupClick = useCallback(
    (popup: PopupData) => {
      track('editor_popup_popup_clicked', {
        popup_id: popup.id,
        popup_name: popup.name,
      });

      sendMessageToIframe({
        eventName: 'showPopup',
        data: { popupId: popup.id },
      });

      setPreviewingPopupIds((prev) => new Set(prev).add(popup.id));
      setSelectedPopup(popup);
      setSelectedSection(undefined);
      setCurrentMode('edit');
      setSelectedTab('triggers');
    },
    [track],
  );

  const onPopupChange = useCallback(
    (updatedPopup: PopupData) => {
      if (!dynamicPageId || !websiteId || !onUpdateWebsiteEvent) return;

      const wu = createWebsiteUpdate(
        dynamicPageId,
        websiteId,
        updatedPopup.id,
        '',
        updatedPopup,
        'updatePopup',
        [],
        website?.development
          ? JSON.parse(JSON.stringify(website?.development))
          : undefined,
      );

      if (!wu) return;

      onUpdateWebsiteEvent(wu);
      setSelectedPopup(updatedPopup);
    },
    [
      dynamicPageId,
      websiteId,
      createWebsiteUpdate,
      website?.development,
      onUpdateWebsiteEvent,
    ],
  );

  const onSectionsReorder = useCallback(
    (items: SectionData[]) => {
      if (!dynamicPageId || !websiteId || !onUpdateWebsiteEvent) return;

      track('editor_popup_sections_reordered', {
        sections_count: items.length,
        website_id: websiteId,
      });

      const wu = createWebsiteUpdate(
        dynamicPageId,
        websiteId,
        '',
        '',
        items.map((item) => ({ id: item.id })),
        'reorderSections',
        [],
        website?.development
          ? JSON.parse(JSON.stringify(website?.development))
          : undefined,
      );

      if (!wu) return;

      onUpdateWebsiteEvent(wu);
    },
    [
      dynamicPageId,
      createWebsiteUpdate,
      websiteId,
      website?.development,
      onUpdateWebsiteEvent,
      track,
    ],
  );

  const onSectionDelete = useCallback(
    (section: SectionData) => {
      if (!dynamicPageId || !websiteId) return;

      track('editor_popup_section_delete_initiated', {
        section_id: section.id,
        section_type: section.type,
        section_variant: section.variant,
      });

      // Create the website update for deletion
      const wu = createWebsiteUpdate(
        dynamicPageId,
        websiteId,
        section.id,
        '',
        '',
        'deleteSection',
        [],
        website?.development
          ? JSON.parse(JSON.stringify(website?.development))
          : undefined,
      );

      if (!wu) return;

      // Set the active website update and open the confirmation modal
      setActiveWebsiteUpdate(wu);
      setDeleteSectionModalOpen(true);
    },
    [
      dynamicPageId,
      createWebsiteUpdate,
      websiteId,
      website?.development,
      setActiveWebsiteUpdate,
      setDeleteSectionModalOpen,
      track,
    ],
  );

  const handleBackToList = useCallback(() => {
    track('editor_popup_back_to_list_clicked');

    // Hide popup preview when going back
    if (selectedPopup) {
      sendMessageToIframe({
        eventName: 'hidePopup',
        data: { popupId: selectedPopup.id },
      });
      setPreviewingPopupIds((prev) => {
        const next = new Set(prev);
        next.delete(selectedPopup.id);
        return next;
      });
    }

    setCurrentMode('list');
    setSelectedSection(undefined);
    setSelectedPopup(undefined);
  }, [track, selectedPopup]);

  const getTitle = () => {
    if (currentMode === 'list') {
      return 'Sections';
    }
    if (selectedPopup) {
      return selectedPopup.name || 'Edit Popup';
    }
    return 'Edit Section';
  };

  return (
    <>
      <Transition
        show={open ?? false}
        enter="ease-out duration-300"
        enterFrom="opacity-0 translate-x-4 sm:translate-x-0 sm:scale-95"
        enterTo="opacity-100 translate-y-0 sm:scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 translate-x-0 sm:scale-100"
        leaveTo="opacity-0 translate-x-4 sm:translate-x-0 sm:scale-95"
        className={classNames(
          'rounded-lg bg-white shadow-2xl',
          'max-h-[90%] min-h-[50%]',
          'w-80',
          'absolute top-2',
          'overflow-x-hidden',
          position === 'left' ? 'left-4' : 'right-4',
        )}
      >
        <div className="sticky top-0 z-50 bg-white">
          <div className="flex items-center justify-between border-b border-gray-200 px-2 pt-3 pb-2">
            <div className="flex items-center gap-2">
              {currentMode === 'edit' ? (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={handleBackToList}
                >
                  <ChevronLeftIcon className="h-4 w-4 text-gray-500" />
                </Button>
              ) : (
                <div />
              )}
              <h3 className="text-base leading-6 font-semibold text-gray-900">
                {getTitle()}
              </h3>
            </div>
            <div className="flex items-center gap-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  setPosition(position === 'right' ? 'left' : 'right')
                }
              >
                <ArrowRightStartOnRectangleIcon
                  className={classNames(
                    'h-5 w-5 text-gray-500',
                    position === 'right' ? 'rotate-180' : 'rotate-0',
                  )}
                  aria-hidden="true"
                />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onClose()}>
                <XMarkIcon
                  className="h-5 w-5 text-gray-500"
                  aria-hidden="true"
                />
              </Button>
            </div>
          </div>
          {currentMode === 'edit' && (
            <MenuTabs
              tabs={tabs}
              onTabClick={(id) => setSelectedTab(id)}
              fullWidth
              size="sm"
            />
          )}
        </div>

        <div className="flex w-full flex-col gap-2">
          {currentMode === 'list' ? (
            <>
              <SectionsList
                sections={sections}
                onSectionClick={onSectionClick}
                onSectionsReorder={onSectionsReorder}
                onSectionRename={(section) => {
                  setSectionToRename(section);
                  setRenameModalOpen(true);
                }}
                onSectionDelete={onSectionDelete}
              />
              <PopupsList
                popups={website?.development?.popups ?? []}
                previewingPopupIds={previewingPopupIds}
                onPopupClick={onPopupClick}
                onPopupVisibilityToggle={(popup) => {
                  const isPreviewing = previewingPopupIds.has(popup.id);
                  sendMessageToIframe({
                    eventName: isPreviewing ? 'hidePopup' : 'showPopup',
                    data: { popupId: popup.id },
                  });
                  setPreviewingPopupIds((prev) => {
                    const next = new Set(prev);
                    if (isPreviewing) {
                      next.delete(popup.id);
                    } else {
                      next.add(popup.id);
                    }
                    return next;
                  });
                }}
                onPopupDelete={(popup) => {
                  if (!dynamicPageId || !websiteId || !onUpdateWebsiteEvent)
                    return;
                  const wu = createWebsiteUpdate(
                    dynamicPageId,
                    websiteId,
                    popup.id,
                    '',
                    null,
                    'deletePopup',
                    [],
                    website?.development
                      ? JSON.parse(JSON.stringify(website?.development))
                      : undefined,
                  );
                  if (wu) onUpdateWebsiteEvent(wu);
                }}
              />
            </>
          ) : selectedPopup ? (
            <>
              {selectedTab === 'triggers' ? (
                <PopupTriggersTab
                  popup={selectedPopup}
                  onChange={onPopupChange}
                />
              ) : (
                <></>
              )}
              {selectedTab === 'content' ? (
                <PopupContentTab
                  popup={selectedPopup}
                  onChange={onPopupChange}
                />
              ) : (
                <></>
              )}
            </>
          ) : (
            selectedSection && (
              <>
                {selectedTab === 'content' ? (
                  <ContentTab section={selectedSection} onChange={onChange} />
                ) : (
                  <></>
                )}
                {selectedTab === 'style' ? (
                  <StyleTab section={selectedSection} onChange={onChange} />
                ) : (
                  <></>
                )}
              </>
            )
          )}
        </div>
      </Transition>
      <RenameSectionModal
        open={renameModalOpen}
        section={sectionToRename}
        onClose={() => {
          setRenameModalOpen(false);
          setSectionToRename(undefined);
        }}
        onRename={(newName) => {
          if (sectionToRename) {
            onChange({
              ...sectionToRename,
              name: newName,
            });
          }
          setRenameModalOpen(false);
          setSectionToRename(undefined);
        }}
      />
    </>
  );
}

interface SectionsListProps {
  sections: SectionData[];
  onSectionClick: (section: SectionData) => void;
  onSectionsReorder: (sections: SectionData[]) => void;
  onSectionRename: (section: SectionData) => void;
  onSectionDelete: (section: SectionData) => void;
}

const SectionsList = ({
  sections,
  onSectionClick,
  onSectionsReorder,
  onSectionRename,
  onSectionDelete,
}: SectionsListProps) => {
  return (
    <div className="px-2 pt-3">
      <SortableList
        items={sections ?? []}
        dividerClassName="flex flex-col gap-1"
        itemClassName="h-9 rounded-lg bg-[#f2f4f7] gap-2 px-2 py-1.5 min-h-0 cursor-pointer"
        onItemClick={onSectionClick}
        renderItemContent={(section) => {
          return (
            <span className="truncate text-sm text-[#141c25]">
              {section.name || capitalize(section.type)}
            </span>
          );
        }}
        renderActions={(section) => (
          <div className="me-1">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size={'icon-sm'}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVert className="h-4 w-4 text-gray-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onSectionRename(section);
                  }}
                >
                  <EditPencil className={'size-4'} />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSectionDelete(section);
                  }}
                >
                  <Trash className={'size-4'} />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        onChange={onSectionsReorder}
      />
    </div>
  );
};

// ============================================================================
// Popups List Component
// ============================================================================

interface PopupsListProps {
  popups: PopupData[];
  previewingPopupIds: Set<string>;
  onPopupClick: (popup: PopupData) => void;
  onPopupVisibilityToggle: (popup: PopupData) => void;
  onPopupDelete: (popup: PopupData) => void;
}

const PopupsList = ({
  popups,
  previewingPopupIds,
  onPopupClick,
  onPopupVisibilityToggle,
  onPopupDelete,
}: PopupsListProps) => {
  if (popups.length === 0) {
    return (
      <div className="px-2 pt-3">
        <div className="rounded-lg border border-dashed border-gray-200 px-3 py-3">
          <h4 className="text-sm font-medium text-gray-700">Popups</h4>
          <p className="mt-1 text-xs text-gray-400">
            No popups yet. Ask the AI to create one!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 py-3">
      <h4 className="mb-1 px-2 text-xs font-medium text-gray-400">Popups</h4>
      <div className="flex flex-col gap-1">
        {popups.map((popup) => (
          <div
            key={popup.id}
            className="group/popup flex h-9 cursor-pointer items-center gap-2 rounded-lg bg-[#f2f4f7] px-2 py-1.5"
            onClick={() => onPopupClick(popup)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="h-4 w-4 shrink-0"
            >
              <rect
                x="2"
                y="2"
                width="12"
                height="12"
                rx="2"
                stroke="#141C25"
                strokeWidth="1.2"
              />
              <rect x="4" y="4" width="8" height="2" rx="0.5" fill="#141C25" />
            </svg>
            <span
              className={classNames(
                'flex-1 truncate text-sm',
                popup.enabled ? 'text-[#141c25]' : 'text-gray-400',
              )}
            >
              {popup.name}
            </span>
            <div className="invisible flex items-center gap-0.5 group-hover/popup:visible">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onPopupVisibilityToggle(popup);
                }}
                title={
                  previewingPopupIds.has(popup.id)
                    ? 'Hide preview'
                    : 'Show preview'
                }
              >
                {previewingPopupIds.has(popup.id) ? (
                  <Eye className="h-3.5 w-3.5 text-gray-500" />
                ) : (
                  <EyeClosed className="h-3.5 w-3.5 text-gray-400" />
                )}
              </Button>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVert className="h-3.5 w-3.5 text-gray-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenuItem
                    className="text-red-500"
                    onClick={() => onPopupDelete(popup)}
                  >
                    <Trash className="size-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Content Tab Component
// ============================================================================

interface ContentTabProps {
  section: SectionData;
  onChange: (_: SectionData) => void;
}

const ContentTab = ({ section, onChange }: ContentTabProps) => {
  const { getSectionTypeConfig } = useDashboardContext();

  const sectionTypeConfig = useMemo(
    () => getSectionTypeConfig(section.type),
    [section.type, getSectionTypeConfig],
  );

  if (!sectionTypeConfig) {
    return (
      <div className="px-4 pt-4">
        <h1>Section Type Not Found</h1>
      </div>
    );
  }

  return (
    <SectionStructureForm
      content={section}
      fields={sectionTypeConfig.structure.fields}
      onChange={onChange}
      isSection
      className="px-4 py-4"
    />
  );
};

// ============================================================================
// Popup Triggers Tab Component
// ============================================================================

interface PopupTriggersTabProps {
  popup: PopupData;
  onChange: (popup: PopupData) => void;
}

const PopupTriggersTab = ({ popup, onChange }: PopupTriggersTabProps) => {
  const triggers = popup.triggers ?? {};

  // Helper to update triggers, stripping undefined values to prevent Firestore save errors
  const updateTriggers = useCallback(
    (updates: Partial<NonNullable<PopupData['triggers']>>) => {
      const merged = { ...(popup.triggers ?? {}), ...updates };
      // JSON round-trip strips undefined values which Firestore cannot store
      const cleaned = JSON.parse(JSON.stringify(merged));
      onChange({ ...popup, triggers: cleaned });
    },
    [popup, onChange],
  );

  // Resolve scroll depth — normalize legacy single number to per-device
  const scrollDepth: ScrollDepthPerDevice | undefined =
    typeof triggers.scrollDepth === 'number'
      ? {
          desktop: triggers.scrollDepth,
          tablet: triggers.scrollDepth,
          mobile: triggers.scrollDepth,
        }
      : triggers.scrollDepth;

  const isTimeDelayEnabled = triggers.timeDelay != null;
  const isScrollDepthEnabled = scrollDepth != null;
  const isExitIntentEnabled = triggers.exitIntent === true;
  // const isClickSelectorEnabled =
  //   triggers.clickSelector != null && triggers.clickSelector !== '';

  // // Debounced local state for click selector text input
  // const [clickSelectorValue, setClickSelectorValue] = useState(
  //   triggers.clickSelector ?? '',
  // );
  // const clickSelectorTimerRef =
  //   useRef<ReturnType<typeof setTimeout>>(undefined);

  // // Sync click selector from parent (e.g. when toggled on/off)
  // useEffect(() => {
  //   setClickSelectorValue(triggers.clickSelector ?? '');
  // }, [triggers.clickSelector]);

  // // Cleanup debounce timer on unmount
  // useEffect(() => {
  //   return () => {
  //     if (clickSelectorTimerRef.current)
  //       clearTimeout(clickSelectorTimerRef.current);
  //   };
  // }, []);

  return (
    <div className="space-y-4 p-4">
      {/* Time Delay */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Timer className="h-4 w-4 text-gray-500" />
            <FieldTitle title="Time Delay" />
            <SettingsHelpTooltip
              iconSize="sm"
              text="Show the popup after the visitor has been on the page for this many seconds."
            />
          </div>
          <Switch
            checked={isTimeDelayEnabled}
            onCheckedChange={(checked) => {
              updateTriggers({ timeDelay: checked ? 5 : undefined });
            }}
          />
        </div>
        {isTimeDelayEnabled && (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              placeholder="5"
              size="thin"
              value={triggers.timeDelay ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                updateTriggers({
                  timeDelay: val === '' ? undefined : Number(val),
                });
              }}
              className="flex-1"
            />
            <span className="text-sm text-gray-500">seconds</span>
          </div>
        )}
      </div>

      <Separator />

      {/* Scroll Depth */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MouseScrollWheel className="h-4 w-4 text-gray-500" />
            <FieldTitle title="Scroll Depth" />
            <SettingsHelpTooltip
              iconSize="sm"
              text="Show the popup after the visitor scrolls past a percentage of the page. Set different thresholds per device."
            />
          </div>
          <Switch
            checked={isScrollDepthEnabled}
            onCheckedChange={(checked) => {
              updateTriggers({
                scrollDepth: checked
                  ? { desktop: 50, tablet: 50, mobile: 50 }
                  : undefined,
              });
            }}
          />
        </div>
        {isScrollDepthEnabled && scrollDepth && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <Laptop className="size-4 text-gray-400" />
              <Input
                type="number"
                min={0}
                max={100}
                size="thin"
                placeholder="50"
                value={scrollDepth.desktop ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  updateTriggers({
                    scrollDepth: {
                      ...scrollDepth,
                      desktop: val === '' ? undefined : Number(val),
                    },
                  });
                }}
              />
              <span className="text-xs text-gray-400">%</span>
            </div>
            <div className="flex items-center gap-1">
              <TabletIcon className="size-4 text-gray-400" />
              <Input
                type="number"
                min={0}
                max={100}
                size="thin"
                placeholder="50"
                value={scrollDepth.tablet ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  updateTriggers({
                    scrollDepth: {
                      ...scrollDepth,
                      tablet: val === '' ? undefined : Number(val),
                    },
                  });
                }}
              />
              <span className="text-xs text-gray-400">%</span>
            </div>
            <div className="flex items-center gap-1">
              <SmartphoneDevice className="size-4 text-gray-400" />
              <Input
                type="number"
                min={0}
                max={100}
                size="thin"
                placeholder="50"
                value={scrollDepth.mobile ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  updateTriggers({
                    scrollDepth: {
                      ...scrollDepth,
                      mobile: val === '' ? undefined : Number(val),
                    },
                  });
                }}
              />
              <span className="text-xs text-gray-400">%</span>
            </div>
          </div>
        )}
      </div>

      <Separator />

      {/* Exit Intent */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CursorPointer className="h-4 w-4 text-gray-500" />
          <FieldTitle title="Exit Intent" />
          <SettingsHelpTooltip
            iconSize="sm"
            text="Show the popup when the visitor moves their cursor toward the browser bar, signaling they're about to leave."
          />
        </div>
        <Switch
          checked={isExitIntentEnabled}
          onCheckedChange={(checked) => {
            updateTriggers({ exitIntent: checked || undefined });
          }}
        />
      </div>

      <Separator />

      {/* Click Selector */}
      {/* <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg
              className="h-4 w-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59"
              />
            </svg>
            <FieldTitle title="Click on Element" />
            <SettingsHelpTooltip
              iconSize="sm"
              text="Show the popup when the visitor clicks an element matching this CSS selector (e.g. a button or link on the page)."
            />
          </div>
          <Switch
            checked={isClickSelectorEnabled}
            onCheckedChange={(checked) => {
              updateTriggers({
                clickSelector: checked ? '#element-id' : undefined,
              });
            }}
          />
        </div>
        {isClickSelectorEnabled && (
          <Input
            type="text"
            placeholder='e.g. [data-popup="my-popup"]'
            value={clickSelectorValue}
            size="thin"
            onChange={(e) => {
              const val = e.target.value;
              setClickSelectorValue(val);
              clearTimeout(clickSelectorTimerRef.current);
              clickSelectorTimerRef.current = setTimeout(() => {
                updateTriggers({
                  clickSelector: val || undefined,
                });
              }, 500);
            }}
          />
        )}
      </div> */}
    </div>
  );
};

// ============================================================================
// Popup Content Tab Component
// ============================================================================

interface PopupContentTabProps {
  popup: PopupData;
  onChange: (popup: PopupData) => void;
}

const PopupContentTab = ({ popup, onChange }: PopupContentTabProps) => {
  return (
    <div className="space-y-3 p-4">
      <div className="space-y-1.5">
        <FieldTitle title="Popup Name" />
        <Input
          type="text"
          value={popup.name}
          onChange={(e) => {
            onChange({ ...popup, name: e.target.value });
          }}
        />
      </div>

      <Separator />

      <CodeInput
        title="HTML Content"
        value={popup.content}
        onChange={(code) => onChange({ ...popup, content: code })}
        language="html"
        readOnly
        inlineLines={12}
      />
    </div>
  );
};

interface StyleTabProps {
  section: SectionData;
  onChange: (_: SectionData) => void;
}

export const StyleTab = ({ section, onChange }: StyleTabProps) => {
  const spacingOptions = useMemo(
    () => [
      {
        value: 'default',
        child: 'Default',
      },
      {
        value: 'none',
        child: 'None',
      },
      ...[
        2, 4, 6, 8, 12, 16, 20, 24, 32, 48, 56, 64, 72, 80, 96, 112, 128, 144,
        160, 176, 192, 208, 224, 240, 256,
      ].map((v) => ({
        value: v,
        child: `${v}px`,
      })),
    ],
    [],
  );
  const maxContentWidthOptions = useMemo(
    () => [
      {
        value: 'default' as const,
        child: 'Default',
      },
      {
        value: 0,
        child: 'None',
      },
      ...[576, 672, 768, 896, 1024, 1152, 1280, 1440, 1650, 1920].map((v) => ({
        value: v,
        child: `${v}px`,
      })),
    ],
    [],
  );

  return (
    <div className="space-y-4 p-4">
      <div>
        <FieldTitle className="mb-2" title={'Background'} />

        <Select
          value={section?.background?.type || 'color'}
          onValueChange={(selected: string) => {
            if (selected === section?.background?.type) return;

            if (selected === 'color') {
              onChange({
                ...section,
                background: null as any,
              });
            } else if (selected === 'gradient') {
              onChange({
                ...section,
                background: {
                  type: 'gradient',
                  colors: [
                    (section.background &&
                      'color' in section.background &&
                      section.background.color) ||
                      '#ccc',
                    '#fff',
                  ],
                  direction: 'to-right',
                },
              });
            }
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="color">Color</SelectItem>
            <SelectItem value="gradient">Gradient</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {section.background?.type !== 'gradient' ? (
        <ColorPickerField
          variant="field"
          allowDelete
          value={section.background?.color}
          defaultColor="white"
          defaultPlaceholder="Default"
          onChange={(color) => {
            onChange({
              ...section,
              background: (color
                ? {
                    type: 'color',
                    color: color,
                  }
                : null) as any,
            });
          }}
        />
      ) : (
        <></>
      )}
      {section.background?.type === 'gradient' ? (
        <>
          <ColorPickerField
            variant="field"
            value={section.background?.colors?.[0]}
            defaultColor="white"
            defaultPlaceholder="Default"
            onChange={(color) => {
              const background = section.background;
              if (!background || background.type !== 'gradient') return;

              onChange({
                ...section,
                background: {
                  ...background,
                  colors: [color, background.colors?.[1] || '#fff'],
                },
              });
            }}
          />

          <ColorPickerField
            variant="field"
            value={section.background?.colors?.[1]}
            defaultColor="white"
            defaultPlaceholder="Default"
            onChange={(color) => {
              const background = section.background;
              if (!background || background.type !== 'gradient') return;

              onChange({
                ...section,
                background: {
                  ...background,
                  colors: [background.colors?.[0] || '#fff', color],
                },
              });
            }}
          />

          <div className="w-full" style={{ zoom: 0.8 }}>
            <ToggleGroup
              type="single"
              variant="outline"
              value={section.background?.direction ?? 'to-right'}
              onValueChange={(
                selected: SectionBackgroundGradient['direction'],
              ) => {
                if (!selected) return;
                const background = section.background;
                if (!background || background.type !== 'gradient') return;

                onChange({
                  ...section,
                  background: {
                    ...background,
                    direction: selected,
                  },
                });
              }}
            >
              <ToggleGroupItem value="to-top">
                <ArrowUpIcon className="h-5 w-5" />
              </ToggleGroupItem>
              <ToggleGroupItem value="to-top-right">
                <ArrowUpRightIcon className="h-5 w-5" />
              </ToggleGroupItem>
              <ToggleGroupItem value="to-right">
                <ArrowRightIcon className="h-5 w-5" />
              </ToggleGroupItem>
              <ToggleGroupItem value="to-bottom-right">
                <ArrowDownRightIcon className="h-5 w-5" />
              </ToggleGroupItem>
              <ToggleGroupItem value="to-bottom">
                <ArrowDownIcon className="h-5 w-5" />
              </ToggleGroupItem>
              <ToggleGroupItem value="to-bottom-left">
                <ArrowDownLeftIcon className="h-5 w-5" />
              </ToggleGroupItem>
              <ToggleGroupItem value="to-left">
                <ArrowLeftIcon className="h-5 w-5" />
              </ToggleGroupItem>
              <ToggleGroupItem value="to-top-left">
                <ArrowUpLeftIcon className="h-5 w-5" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </>
      ) : (
        <></>
      )}
      <ColorPickerField
        title="Text"
        variant="field"
        allowDelete
        value={section.textColor}
        defaultColor="white"
        defaultPlaceholder="Default"
        onChange={(color) => {
          onChange({
            ...section,
            textColor: color as any,
          });
        }}
      />
      <div className="-mx-4">
        <Separator />
      </div>

      <div className="mt-2 flex flex-row items-center justify-between">
        <FieldTitle title={'Background Image'} />

        <Switch
          checked={section.backgroundImage?.show ?? false}
          onCheckedChange={(checked) => {
            onChange({
              ...section,
              backgroundImage: { ...section.backgroundImage, show: checked },
            });
          }}
        />
      </div>
      {section.backgroundImage?.show ? (
        <FileUploadInput
          image={section?.backgroundImage?.image}
          onSelected={(image) => {
            onChange({
              ...section,
              backgroundImage: {
                image: (image || null) as any,
                show: section.backgroundImage?.show,
              },
            });
          }}
          showImageSettings
        />
      ) : (
        <></>
      )}

      <div className="-mx-4">
        <Separator />
      </div>

      <div>
        <FieldTitle className="mb-2" title={'Spacing Top'} />

        <Select
          value={String(section?.spacing?.top || 'default')}
          onValueChange={(selected: string) => {
            onChange({
              ...section,
              spacing: {
                ...section.spacing,
                top: (!selected || selected === 'default'
                  ? null
                  : selected) as any,
              },
            });
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {spacingOptions.map((opt) => (
              <SelectItem key={String(opt.value)} value={String(opt.value)}>
                {opt.child}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <FieldTitle className="mb-2" title={'Spacing Bottom'} />

        <Select
          value={String(section?.spacing?.bottom || 'default')}
          onValueChange={(selected: string) => {
            onChange({
              ...section,
              spacing: {
                ...section.spacing,
                bottom: (!selected || selected === 'default'
                  ? null
                  : selected) as any,
              },
            });
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {spacingOptions.map((opt) => (
              <SelectItem key={String(opt.value)} value={String(opt.value)}>
                {opt.child}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <FieldTitle className="mb-2" title={'Max Content Width'} />

        <Select
          value={String(section?.maxContentWidth ?? 'default')}
          onValueChange={(selected: string) => {
            onChange({
              ...section,
              maxContentWidth: (selected === 'default'
                ? null
                : selected) as any,
            });
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {maxContentWidthOptions.map((opt) => (
              <SelectItem key={String(opt.value)} value={String(opt.value)}>
                {opt.child}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-2 flex flex-row items-center justify-between">
        <FieldTitle title={'Align'} />

        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={section?.mainAlignment ?? 'default'}
          onValueChange={(selected: string) => {
            if (!selected) return;
            onChange({
              ...section,
              mainAlignment: (!selected || selected === 'default'
                ? null
                : selected) as any,
            });
          }}
        >
          <ToggleGroupItem value="default">Default</ToggleGroupItem>
          <ToggleGroupItem value="center">Center</ToggleGroupItem>
          <ToggleGroupItem value="start">Start</ToggleGroupItem>
          <ToggleGroupItem value="end">End</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <div className="mt-2 flex flex-row items-center justify-between">
        <FieldTitle title={'Min Height'} />

        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={section?.minHeight ?? 'default'}
          onValueChange={(selected: string) => {
            if (!selected) return;
            onChange({
              ...section,
              minHeight: (!selected || selected === 'default'
                ? null
                : selected) as any,
            });
          }}
        >
          <ToggleGroupItem value="default">Default</ToggleGroupItem>
          <ToggleGroupItem value="auto">Content</ToggleGroupItem>
          <ToggleGroupItem value="screen">Screen</ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* <div className="mt-2 flex flex-row justify-between items-center">
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Divider
        </label>

        <ToggleButton<SectionData['divider']>
          value={section?.divider ?? 'none'}
          size="x-small"
          items={[
            { value: 'none', title: 'None' },
            { value: 'linear', title: 'Linear' },
            { value: 'curved', title: 'Curved' },
            { value: 'wave', title: 'Wave' },
          ]}
          onChange={(selected) => {
            onChange({
              ...section,
              divider: selected,
            });
          }}
        />
      </div> */}
    </div>
  );
};

interface RenameSectionModalProps {
  open: boolean;
  section?: SectionData;
  onClose: () => void;
  onRename: (newName: string) => void;
}

const RenameSectionModal = ({
  open,
  section,
  onClose,
  onRename,
}: RenameSectionModalProps) => {
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (open && section) {
      setNewName(section.name || capitalize(section.type));
    }
  }, [open, section]);

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (newName.trim()) {
      onRename(newName.trim());
      setNewName('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename Section</DialogTitle>
          <DialogDescription>
            Enter a new name for the section &quot;
            {section?.name || capitalize(section?.type || '')}&quot;
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="section-name"
                className="mb-2 block text-sm leading-6 font-medium text-gray-900"
              >
                Section Name
              </label>
              <input
                id="section-name"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none sm:text-sm"
                placeholder="Enter section name"
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onClose();
                setNewName('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!newName.trim()}
              onClick={handleSubmit}
            >
              Rename
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
